/**
 * WekaTrust Landing Page
 * Green & black theme. Bold, scroll-driven, bento layout.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../providers';
import { useI18n } from '../providers';
import SEO from '../components/seo/SEO';
import { generateOrganizationSchema, generateWebSiteSchema, generateServiceSchema, generateHowToSchema } from '../utils/seo';
import { PublicHeader, PublicFooter } from '../components/public';
import '../styles/landing.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { language, formatCurrency } = useI18n();
  const [stats, setStats] = useState({ transactions: 0, users: 0, successRate: 0, volume: 0 });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [howInView, setHowInView] = useState(false);
  const howRef = useRef(null);

  // Logged-in users should see the dashboard, not the landing page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null; // Redirecting to dashboard; avoid flash of landing content
  }

  const testimonials = [
    { name: 'Sarah M.', role: language === 'fr' ? 'Designer Freelance' : 'Freelance Designer', text: language === 'fr' ? 'WekaTrust m\'a permis de recevoir mes paiements en toute sécurité. Plus de problèmes avec les clients qui disparaissent!' : 'WekaTrust has allowed me to receive payments securely. No more issues with disappearing clients!', rating: 5 },
    { name: 'Jean K.', role: language === 'fr' ? 'Petit Commerçant' : 'Small Business Owner', text: language === 'fr' ? 'Le processus est si simple. 3 étapes et c\'est fait. Mes clients adorent!' : 'The process is so simple. 3 steps and done. My customers love it!', rating: 5 },
    { name: 'Marie T.', role: language === 'fr' ? 'Acheteuse' : 'Buyer', text: language === 'fr' ? 'Enfin une solution qui protège vraiment les acheteurs. Je me sens en sécurité!' : 'Finally a solution that really protects buyers. I feel safe!', rating: 5 },
  ];

  const threeTapsSteps = [
    { number: '1', icon: '➕', title: language === 'fr' ? 'Créer' : 'Create', description: language === 'fr' ? 'Créez votre transaction en quelques secondes' : 'Create your transaction in seconds' },
    { number: '2', icon: '📤', title: language === 'fr' ? 'Partager' : 'Share', description: language === 'fr' ? 'Partagez le lien via WhatsApp, Instagram, etc.' : 'Share the link via WhatsApp, Instagram, etc.' },
    { number: '3', icon: '✅', title: language === 'fr' ? 'Sécuriser' : 'Secure', description: language === 'fr' ? 'L\'argent est protégé jusqu\'à la livraison' : 'Money is protected until delivery' },
  ];

  const features = [
    { icon: '🔒', title: language === 'fr' ? 'Protection Escrow' : 'Escrow Protection', description: language === 'fr' ? 'L\'argent est sécurisé jusqu\'à la confirmation de livraison' : 'Money held safely until delivery confirmed' },
    { icon: '📱', title: language === 'fr' ? 'Mobile-First' : 'Mobile-First', description: language === 'fr' ? 'Optimisé pour MTN Mobile Money, Orange Money et les paiements mobiles' : 'Optimized for MTN Mobile Money, Orange Money, and mobile payments' },
    { icon: '⚡', title: language === 'fr' ? 'Résolution Rapide' : 'Fast Resolution', description: language === 'fr' ? 'Médiation assistée par IA dans les 48 heures' : 'AI-assisted dispute mediation within 48 hours' },
    { icon: '💰', title: language === 'fr' ? 'Frais Transparents' : 'Transparent Fees', description: language === 'fr' ? '1.5% de frais de transaction, plafonné à 15,000 XAF' : '1.5% transaction fee, capped at 15,000 XAF' },
    { icon: '🤝', title: language === 'fr' ? 'Construire la Confiance' : 'Build Trust', description: language === 'fr' ? 'Système de vérification et historique des transactions' : 'Verification system and transaction history' },
    { icon: '🌍', title: language === 'fr' ? 'Cameroun & Afrique' : 'Cameroon & Africa', description: language === 'fr' ? 'Conçu pour le Cameroun, fonctionne dans toute l\'Afrique' : 'Built for Cameroon, works across Africa' },
  ];

  useEffect(() => {
    setStats({ transactions: 12500, users: 5200, successRate: 98, volume: 50000000 });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  useEffect(() => {
    const el = howRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setHowInView(true)); },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const structuredData = [
    generateOrganizationSchema(),
    generateWebSiteSchema(),
    generateServiceSchema(),
    generateHowToSchema([
      { name: language === 'fr' ? 'Créer une Transaction' : 'Create a Transaction', text: language === 'fr' ? 'Créez une transaction en définissant le titre, le montant et le délai de livraison.' : 'Create a transaction by defining the title, amount, and delivery deadline.' },
      { name: language === 'fr' ? 'Paiement Sécurisé' : 'Secure Payment', text: language === 'fr' ? 'L\'acheteur effectue le paiement via MTN Mobile Money ou Orange Money.' : 'The buyer makes payment via MTN Mobile Money or Orange Money.' },
      { name: language === 'fr' ? 'Livraison et Libération' : 'Delivery & Release', text: language === 'fr' ? 'Le vendeur marque comme livré, l\'acheteur approuve, et les fonds sont libérés.' : 'Seller marks as delivered, buyer approves, and funds are released.' },
    ]),
  ];

  const handleGetStarted = () => (isAuthenticated ? navigate('/dashboard') : navigate('/auth/signup'));
  const handleCreateTransaction = () => (isAuthenticated ? navigate('/transactions/create') : navigate('/auth/signup'));

  return (
    <>
      <SEO
        title={language === 'fr' ? 'WekaTrust - Plateforme d\'Escrow Sécurisée pour le Cameroun' : 'WekaTrust - Secure Escrow Platform for Cameroon'}
        description={language === 'fr' ? 'Transactions peer-to-peer sécurisées avec protection escrow intelligente.' : 'Secure peer-to-peer transactions with intelligent escrow protection.'}
        keywords={language === 'fr' ? 'escrow, Cameroun, mobile money, MTN MoMo, Orange Money' : 'escrow, Cameroon, mobile money, MTN MoMo, Orange Money'}
        url="/"
        locale={language}
        structuredData={structuredData[0]}
      />
      <div className="landing-page landing-wt">
        <PublicHeader />
        <main className="flex-1">
          {/* Hero */}
          <section className="hero-wt">
            <div className="hero-wt-bg" aria-hidden="true" />
            <div className="hero-wt-grid" aria-hidden="true" />
            <div className="hero-wt-noise" aria-hidden="true" />
            <div className="hero-wt-orb hero-wt-orb-1" aria-hidden="true" />
            <div className="hero-wt-orb hero-wt-orb-2" aria-hidden="true" />
            <div className="hero-wt-orb hero-wt-orb-3" aria-hidden="true" />
            <div className="hero-wt-inner">
              <h1 className="hero-wt-title">
                {language === 'fr' ? 'Votre Argent,' : 'Your Money,'} <span>{language === 'fr' ? 'Protégé.' : 'Protected.'}</span>
              </h1>
              <p className="hero-wt-sub">
                {language === 'fr'
                  ? 'Transactions sécurisées peer-to-peer avec protection escrow. Cameroun & Afrique.'
                  : 'Secure peer-to-peer transactions with escrow protection. Cameroon & Africa.'}
              </p>
              <div className="hero-wt-cta">
                <button type="button" className="hero-wt-btn-primary" onClick={handleCreateTransaction}>
                  {language === 'fr' ? 'Créer une Transaction' : 'Create Transaction'}
                </button>
                <button
                  type="button"
                  className="hero-wt-btn-secondary"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {language === 'fr' ? 'En Savoir Plus' : 'Learn More'}
                </button>
              </div>
              <div className="hero-wt-stats">
                <div className="hero-wt-stat">
                  <div className="hero-wt-stat-num">{stats.transactions.toLocaleString()}+</div>
                  <div className="hero-wt-stat-label">{language === 'fr' ? 'Transactions' : 'Transactions'}</div>
                </div>
                <div className="hero-wt-stat">
                  <div className="hero-wt-stat-num">{stats.users.toLocaleString()}+</div>
                  <div className="hero-wt-stat-label">{language === 'fr' ? 'Utilisateurs' : 'Users'}</div>
                </div>
                <div className="hero-wt-stat">
                  <div className="hero-wt-stat-num">{stats.successRate}%</div>
                  <div className="hero-wt-stat-label">{language === 'fr' ? 'Taux de Réussite' : 'Success Rate'}</div>
                </div>
                <div className="hero-wt-stat">
                  <div className="hero-wt-stat-num">{formatCurrency(stats.volume, 'XAF')}</div>
                  <div className="hero-wt-stat-label">{language === 'fr' ? 'Volume Total' : 'Total Volume'}</div>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section id="how-it-works" className="section-wt">
            <div className="section-wt-inner">
              <div className="section-wt-head">
                <h2 className="section-wt-title">{language === 'fr' ? 'Comment ça marche' : 'How It Works'}</h2>
                <p className="section-wt-sub">
                  {language === 'fr' ? 'Créez, partagez, sécurisez — en 3 étapes.' : 'Create, share, secure — in 3 steps.'}
                </p>
              </div>
              <div className={`how-wt-wrap ${howInView ? 'is-inview' : ''}`} ref={howRef}>
                <div className="how-wt-connector" aria-hidden="true" />
                <div className="how-wt-steps">
                  {threeTapsSteps.map((step, i) => (
                    <article key={i} className="how-wt-step">
                      <div className="how-wt-num">{step.number}</div>
                      <div className="how-wt-icon">{step.icon}</div>
                      <h3 className="how-wt-step-title">{step.title}</h3>
                      <p className="how-wt-step-desc">{step.description}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Why WekaTrust - Bento */}
          <section className="section-wt section-wt-alt">
            <div className="section-wt-inner">
              <div className="section-wt-head">
                <h2 className="section-wt-title">{language === 'fr' ? 'Pourquoi WekaTrust?' : 'Why WekaTrust?'}</h2>
                <p className="section-wt-sub">
                  {language === 'fr' ? 'Escrow mobile-first pour le Cameroun et l\'Afrique' : 'Mobile-first escrow for Cameroon and Africa'}
                </p>
              </div>
              <div className="bento-wt">
                {features.map((f, i) => (
                  <article key={i} className="bento-wt-card">
                    <div className="bento-wt-icon">{f.icon}</div>
                    <h3 className="bento-wt-title">{f.title}</h3>
                    <p className="bento-wt-desc">{f.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Cameroon */}
          <section className="section-wt">
            <div className="section-wt-inner">
              <div className="cameroon-wt">
                <h2>{language === 'fr' ? 'Conçu pour le Cameroun' : 'Built for Cameroon'}</h2>
                <p>
                  {language === 'fr'
                    ? 'MTN Mobile Money, Orange Money, XAF. Interface bilingue français/anglais.'
                    : 'MTN Mobile Money, Orange Money, XAF. Bilingual French/English.'}
                </p>
                <div className="cameroon-wt-badges">
                  <span className="cameroon-wt-badge">MTN Mobile Money</span>
                  <span className="cameroon-wt-badge">Orange Money</span>
                  <span className="cameroon-wt-badge">XAF</span>
                  <span className="cameroon-wt-badge">FR / EN</span>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="section-wt section-wt-alt">
            <div className="section-wt-inner">
              <div className="section-wt-head">
                <h2 className="section-wt-title">{language === 'fr' ? 'Ce Que Disent Nos Utilisateurs' : 'What Our Users Say'}</h2>
                <p className="section-wt-sub">{language === 'fr' ? 'Ils nous font confiance' : 'Trusted by thousands'}</p>
              </div>
              <div className="testimonial-wt-wrap">
                <div className="testimonial-wt-track">
                  <div className="testimonial-wt-inner" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                    {testimonials.map((t, i) => (
                      <div key={i} className="testimonial-wt-slide">
                        <article className="testimonial-wt-card">
                          <div className="testimonial-wt-stars" aria-hidden="true">
                            {[...Array(t.rating)].map((_, j) => <span key={j}>★</span>)}
                          </div>
                          <p className="testimonial-wt-quote">&ldquo;{t.text}&rdquo;</p>
                          <div className="testimonial-wt-author">
                            <div className="testimonial-wt-avatar" aria-hidden="true">{t.name.charAt(0)}</div>
                            <div>
                              <p className="testimonial-wt-name">{t.name}</p>
                              <p className="testimonial-wt-role">{t.role}</p>
                            </div>
                          </div>
                        </article>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="testimonial-wt-nav">
                  <button type="button" className="testimonial-wt-btn" onClick={() => setActiveTestimonial((p) => (p === 0 ? testimonials.length - 1 : p - 1))} aria-label={language === 'fr' ? 'Précédent' : 'Previous'}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                  </button>
                  <div className="testimonial-wt-dots" role="tablist">
                    {testimonials.map((_, i) => (
                      <button key={i} type="button" role="tab" aria-selected={i === activeTestimonial} className={`testimonial-wt-dot ${i === activeTestimonial ? 'is-active' : ''}`} onClick={() => setActiveTestimonial(i)} />
                    ))}
                  </div>
                  <button type="button" className="testimonial-wt-btn" onClick={() => setActiveTestimonial((p) => (p + 1) % testimonials.length)} aria-label={language === 'fr' ? 'Suivant' : 'Next'}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="section-wt">
            <div className="section-wt-inner">
              <div className="cta-wt">
                <h2>{language === 'fr' ? 'Prêt à commencer ?' : 'Ready to get started?'}</h2>
                <p>{language === 'fr' ? 'Rejoignez WekaTrust.' : 'Join WekaTrust.'}</p>
                <div className="cta-wt-btns">
                  <button type="button" className="hero-wt-btn-primary" onClick={handleCreateTransaction}>
                    {language === 'fr' ? 'Créer une transaction' : 'Create transaction'}
                  </button>
                  {!isAuthenticated && (
                    <button type="button" className="hero-wt-btn-secondary" onClick={handleGetStarted}>
                      {language === 'fr' ? 'S\'inscrire' : 'Sign up'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
        <PublicFooter />
      </div>
    </>
  );
};

export default LandingPage;
